import { inject, Injectable } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';
import { NGKIT_ASSET_VERSION } from './ngkit-asset-version.token';

/**
 * Service for resolving versioned asset URLs that respects both the application
 * base href and the optional asset version segment.
 * Use this service instead of constructing asset URLs manually to ensure both
 * tokens are applied consistently.
 * @example
 * ```typescript
 * const assetUrl = inject(NgKitAssetUrlService);
 * const iconUrl = assetUrl.resolveNgKitAsset('icons/brand/wtw.svg');
 * // dev:        '/manager/assets/wtw/angularkit/icons/brand/wtw.svg'
 * // production: '/manager/assets/abc1234/wtw/angularkit/icons/brand/wtw.svg'
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class NgKitAssetUrlService {
  private readonly baseHref = this.ensureTrailingSlash(inject(APP_BASE_HREF, { optional: true }) || '/');
  private readonly assetVersion = this.ensureTrailingSlash(inject(NGKIT_ASSET_VERSION, { optional: true }) ?? '');

  /**
   * Resolves a URL for any asset under the application's assets folder.
   * @param relativePath - Path relative to `{baseHref}assets/`, e.g. `'images/logo.png'`
   * @returns Fully resolved asset URL including base href and asset version segment
   */
  resolveAsset(relativePath: string): string {
    return `${this.baseHref}assets/${this.assetVersion}${relativePath}`;
  }

  /**
   * Resolves a URL within AngularKit's own asset folder (`wtw/angularkit/`).
   * @param path - Path relative to `wtw/angularkit/`, e.g. `'icons/brand/wtw.svg'`
   * @returns Fully resolved AngularKit asset URL
   */
  resolveNgKitAsset(path: string): string {
    return this.resolveAsset(`wtw/angularkit/${path}`);
  }

  /**
   * Ensures a string ends with a trailing slash.
   * Returns an empty string unchanged (preserves optional path segments).
   * @param value - The string to normalise
   * @returns The string with a guaranteed trailing slash, or `""` if empty
   */
  private ensureTrailingSlash(value: string): string {
    if (value.length === 0) return '';
    return value.endsWith('/') ? value : `${value}/`;
  }
}
